/**
 * useAgentFeedWebSocket — React hook that owns the AgentFeedWebSocket instance
 * lifecycle.
 *
 * Mount once at the OrgContext level. Translates inbound WS messages into store
 * mutations and invalidates the useAgentFeed query on reconnect / inactivity so
 * the consumer hook refetches and resyncs the store via applyServerSnapshot().
 */

import { QueryClientContext } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { agentFeedQueryKey } from '../hooks/useAgentFeed';
import {
  addOrUpdateItem,
  completeItem,
  transitionTriggerToConversation,
} from '../store/agentFeedStore';
import { AgentFeedWebSocket } from './AgentFeedWebSocket';
import type { AgentFeedServerMessage, AgentFeedWebSocketStatus } from './types';

interface UseAgentFeedWebSocketOptions {
  orgId: number | null;
  enabled?: boolean;
}

interface UseAgentFeedWebSocketReturn {
  status: AgentFeedWebSocketStatus;
}

export function useAgentFeedWebSocket({
  orgId,
  enabled = false,
}: UseAgentFeedWebSocketOptions): UseAgentFeedWebSocketReturn {
  const queryClient = useContext(QueryClientContext);
  const [status, setStatus] =
    useState<AgentFeedWebSocketStatus>('disconnected');
  const wsRef = useRef<AgentFeedWebSocket | null>(null);

  const handleTriggerPending = useCallback(
    (msg: Extract<AgentFeedServerMessage, { type: 'trigger.pending' }>) => {
      addOrUpdateItem({
        id: `trigger_${msg.trigger_id}`,
        type: 'trigger',
        status: 'evaluating',
        entityId: msg.entity_id,
        entityName: msg.entity_name,
        entityTypeName: msg.entity_type_name,
        triggerSources: msg.trigger_sources,
        fireAt: msg.fire_at ?? undefined,
        triggerId: msg.trigger_id,
        createdAt: new Date().toISOString(),
      });
    },
    []
  );

  const handleAgentStarted = useCallback(
    (msg: Extract<AgentFeedServerMessage, { type: 'agent.started' }>) => {
      if (msg.trigger_id === null) {
        // No tracked trigger — add the conversation item directly
        addOrUpdateItem({
          id: `conv_${msg.conversation_id}`,
          type: 'conversation',
          status: 'preparing',
          entityId: msg.entity_id,
          entityName: msg.entity_name,
          triggerSources: msg.trigger_sources,
          conversationId: msg.conversation_id,
          triggerId: undefined,
          createdAt: new Date().toISOString(),
        });
      } else {
        transitionTriggerToConversation(msg.trigger_id, msg.conversation_id);
      }
    },
    []
  );

  const handleAgentCompleted = useCallback(
    (msg: Extract<AgentFeedServerMessage, { type: 'agent.completed' }>) => {
      completeItem(
        msg.conversation_id,
        msg.outcome,
        msg.proposal_id ?? undefined
      );
    },
    []
  );

  const handleRefreshNeeded = useCallback(() => {
    if (queryClient && orgId) {
      console.warn(
        '[useAgentFeedWebSocket] Invalidating agent feed query (org:',
        orgId,
        ')'
      );
      queryClient.invalidateQueries({ queryKey: agentFeedQueryKey(orgId) });
    }
  }, [queryClient, orgId]);

  useEffect(() => {
    if (!queryClient || !enabled || !orgId) {
      if (wsRef.current) {
        wsRef.current.destroy();
        wsRef.current = null;
        setStatus('disconnected');
      }
      return;
    }

    const ws = new AgentFeedWebSocket({
      orgId,
      onTriggerPending: handleTriggerPending,
      onAgentStarted: handleAgentStarted,
      onAgentCompleted: handleAgentCompleted,
      onReconnected: handleRefreshNeeded,
      onInactivityRefresh: handleRefreshNeeded,
      onStatusChange: setStatus,
    });

    wsRef.current = ws;
    ws.connect();

    return () => {
      ws.destroy();
      wsRef.current = null;
      setStatus('disconnected');
    };
  }, [
    queryClient,
    enabled,
    orgId,
    handleTriggerPending,
    handleAgentStarted,
    handleAgentCompleted,
    handleRefreshNeeded,
  ]);

  return { status };
}
